"use strict"

import React, { useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { Button, Nav, Tab } from "react-bootstrap"

import InventoryItemsManageTable from "./InventoryItemsManageTable"
import ModalCreateInventoryType from "./ModalCreateInventoryType"
import { API_STATUS } from "../../../shared_components/widgets/utils"
import { deepcopy } from "../../../shared_components/utils"
import PageLoadingPanel from "../../../shared_components/widgets/PageLoadingPanel"
import PageErrorPanel from "../../../shared_components/widgets/PageErrorPanel"
import { withInventoryConfig } from "../../../config/InventoryConfigProvider"
import { deleteObject } from "../../../Loaders"
import { logToServer } from "../../../ErrorBoundary"

function ManageInventory({
  intl,
  user_permissions = [],
  inventoryTypes = [],
  getInventoryTypes,
  statusInventoryTypes,
  getInventoryItems,
  inventoryItems = [],
  tempInvTypes = [],
}) {
  const [inventoryFocus, setInventoryFocus] = useState({})
  useEffect(() => {
    setInventoryFocus(inventoryTypes.length > 0 ? inventoryTypes[0] : {})
  }, [inventoryTypes])

  const [modalCreateTypeIsVisible, setModalCreateTypeIsVisible] = useState(false)
  const [grouped_data, setGroupedData] = useState([])

  useEffect(() => {
    let data_grouped = []
    let map_skuid_idx = {}
    let counter_idx = -1
    inventoryItems.forEach((item) => {
      // Check if group exists
      if (!map_skuid_idx.hasOwnProperty(item.sku.id)) {
        data_grouped.push({
          sku: deepcopy(item.sku),
          available_quantity: 0,
          subRows: [],
        })
        counter_idx += 1
        map_skuid_idx[item.sku.id] = counter_idx
      }

      let group_idx = map_skuid_idx[item.sku.id] // Get group idx
      data_grouped[group_idx].subRows.push(item) // Add this item to group
      data_grouped[group_idx].available_quantity += item.available_quantity // Update `available_quantity`
    })

    // Ungroup groups with single item
    data_grouped.forEach((grp, idx) => {
      if (grp.subRows.length === 1) {
        data_grouped[idx] = grp.subRows[0]
      }
    })

    setGroupedData(data_grouped)
  }, [inventoryItems])

  const pageLoad = () => {
    getInventoryTypes()
    getInventoryItems()
  }

  return (
    <>
      <div className={"row"}>
        <div className={"col-8"}>
          <h4>
            <span className={"fa fa-list"} />
            &nbsp;&nbsp;
            <FormattedMessage id={"inventory.manage-inventory"} />
          </h4>
        </div>
        <div className={"col-4"} align={"right"}>
          <Button variant={"outline-primary"} onClick={() => pageLoad()}>
            <span className={"fa fa-refresh"} />
            &nbsp;&nbsp;
            <FormattedMessage id={"general.refresh"} />
          </Button>
        </div>
      </div>

      <hr style={{ margin: "15px 0px 25px 0px" }} />

      <div>
        {statusInventoryTypes === API_STATUS.LOADING && (
          <div className={"card-body"}>
            <PageLoadingPanel />
          </div>
        )}

        {statusInventoryTypes === API_STATUS.ERROR && (
          <div className={"card-body"}>
            <PageErrorPanel attemptReload={pageLoad} />
          </div>
        )}

        {statusInventoryTypes === API_STATUS.SUCCESS && (
          <>
            <Tab.Container defaultActiveKey={"tab0"} style={{ margin: "20px 0px" }}>
              <div className={"row"}>
                <div className={"col-lg-2"} style={{ padding: "0px" }}>
                  <Nav variant={"pills"} className={"flex-column"} style={{ marginBottom: "15px" }}>
                    {inventoryTypes.map((inv, idx) => (
                      <Nav.Link
                        className={"side_nav_pills"}
                        eventKey={`tab${idx}`}
                        onClick={(e) => {
                          e.preventDefault()
                          if (inventoryFocus && inventoryFocus.name && inventoryFocus.name !== inv.name) {
                            setInventoryFocus(inv)
                          }
                        }}
                        key={`${inv.name}_Nav_Option`}
                      >
                        {inv.name}
                        {tempInvTypes.map((t) => t.name).includes(inv.name) && (
                          <Button
                            variant={"outline-primary"}
                            className={"btn-sm"}
                            style={{ float: "right" }}
                            title={intl.formatMessage({ id: "general.delete" })}
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              deleteObject(`/laboratory/inventory/inventory-type/${inv.id}`, false, false)
                                .then((_) => {
                                  getInventoryTypes()
                                })
                                .catch((error) => {
                                  console.error(error)
                                  logToServer(error)
                                  alert(intl.formatMessage({ id: "inventory.error-delete-item" }))
                                })
                            }}
                          >
                            <span className={"fa fa-trash"} />
                          </Button>
                        )}
                      </Nav.Link>
                    ))}
                  </Nav>
                  <Button
                    variant={"outline-primary"}
                    onClick={(e) => {
                      e.preventDefault()
                      setModalCreateTypeIsVisible(true)
                    }}
                    style={{
                      width: "100%",
                      whiteSpace: "normal",
                      marginBottom: "50px",
                    }}
                    disabled={!user_permissions.includes("add_inventorytype")}
                  >
                    <span className={"fa fa-plus-circle"} />
                    &nbsp;
                    <FormattedMessage
                      id={"inventory.add-new-inventory-type"}
                      description={"Register New Inventory Type Button"}
                    />
                  </Button>
                </div>
                <div className={"col-lg-10"}>
                  {inventoryTypes.length > 0 &&
                    inventoryTypes.map((inv, idx) => (
                      <Tab.Content key={`${inv.name}_Tab`}>
                        <Tab.Pane eventKey={`tab${idx}`} transition={false}>
                          {/*---------- Inventory Items ----------*/}
                          <div className={"card"}>
                            <div className={"card-header"}>
                              <h5 className={"card-title"} style={{ margin: "0" }}>
                                <span className={"fa fa-inbox"} />
                                &nbsp;
                                <FormattedMessage id={"inventory.inventory_items"} />
                                {inventoryFocus && inventoryFocus.name
                                  ? ` - ${inventoryFocus.name} (${inventoryFocus.description})`
                                  : ""}
                              </h5>
                            </div>
                            <div className={"card-body"} style={{ padding: "0px" }}>
                              <InventoryItemsManageTable
                                grouped_data={grouped_data}
                                inventory_focus={inventoryFocus}
                              />
                            </div>
                          </div>
                          {/*---------- /Inventory Items ----------*/}
                        </Tab.Pane>
                      </Tab.Content>
                    ))}
                </div>
              </div>
            </Tab.Container>
            {/*----- Modals -----*/}
            {modalCreateTypeIsVisible && user_permissions.includes("add_inventorytype") && (
              <ModalCreateInventoryType
                show={modalCreateTypeIsVisible}
                onHide={() => setModalCreateTypeIsVisible(false)}
                onSuccess={() => {
                  setModalCreateTypeIsVisible(false)
                  getInventoryTypes()
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  )
}

export default withInventoryConfig(ManageInventory)
